import React, { useEffect, useRef, useState, type UIEvent } from "react";
import { Input, Spin, type InputRef } from "antd";
import { useDebounce } from "use-debounce";

import {
  getGifPreview,
  KLIPY_ATTRIBUTION,
  type GifSearchSource,
  type KlipyGif,
} from "@fitness-app/client-shared/features/chat/klipy-gifs";
import { useGifSearch } from "@fitness-app/client-shared/features/chat/use-gif-search";
import { cn } from "@fitness-app/utils/src/styles/cn";

export interface GifPickerSelection {
  gif: KlipyGif;
  query: string;
  source: GifSearchSource;
}

interface GifPickerProps {
  appKey: string;
  customerId?: string;
  onSelect: (selection: GifPickerSelection) => void;
}

const SEARCH_DEBOUNCE_MS = 350;
const LOAD_MORE_THRESHOLD_PX = 160;

/**
 * Trending → search grid over KLIPY. Selecting sends straight away (no "attach then send" step),
 * which is what every messenger does with GIFs and what keeps the picker out of the upload flow.
 */
export const GifPicker = ({ appKey, customerId, onSelect }: GifPickerProps): React.ReactElement => {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const inputRef = useRef<InputRef>(null);
  const { gifs, source, isLoading, isError, isPreviousData, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useGifSearch({ appKey, customerId, query: debouncedQuery });

  useEffect(() => {
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(timeout);
  }, []);

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (hasNextPage && !isFetchingNextPage && scrollTop + clientHeight >= scrollHeight - LOAD_MORE_THRESHOLD_PX) {
      void fetchNextPage();
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Spin />
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-gray-500">
          <span>Nie udało się pobrać GIF-ów.</span>
          <button type="button" className="text-blue-500 hover:underline" onClick={() => void refetch()}>
            Spróbuj ponownie
          </button>
        </div>
      );
    }

    if (!gifs.length) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          Brak GIF-ów dla „{debouncedQuery}”.
        </div>
      );
    }

    // While the previous query's results are still on screen, a tap would send a GIF the user
    // did not search for (and attribute it to the new query) — so the grid dims and ignores clicks.
    return (
      <>
        <div className={cn("grid grid-cols-3 gap-1", isPreviousData && "pointer-events-none opacity-50")}>
          {gifs.map((gif) => {
            const preview = getGifPreview(gif);
            return (
              <button
                key={gif.slug}
                type="button"
                title={gif.title}
                aria-label={`Wyślij GIF: ${gif.title}`}
                disabled={isPreviousData}
                className="h-24 w-full overflow-hidden rounded-md bg-gray-100 outline-none ring-blue-400 transition hover:opacity-90 focus-visible:ring-2"
                onClick={() => onSelect({ gif, query: debouncedQuery, source })}
              >
                {preview ? (
                  <img src={preview.url} alt={gif.title} loading="lazy" className="h-full w-full object-cover" />
                ) : null}
              </button>
            );
          })}
        </div>
        {isFetchingNextPage ? (
          <div className="flex justify-center py-2">
            <Spin size="small" />
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="flex w-[360px] flex-col gap-2" data-testid="gif-picker">
      <Input
        ref={inputRef}
        allowClear
        value={query}
        placeholder="Szukaj GIF-a..."
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="h-[320px] overflow-y-auto" onScroll={onScroll}>
        {renderBody()}
      </div>
      <div className="text-right text-[11px] text-gray-400">{KLIPY_ATTRIBUTION}</div>
    </div>
  );
};
